import React from 'react';
import {
  C,
  LEADERBOARD_TRIGGER_H,
  LEADERBOARD_TRIGGER_W,
  getPathValue,
  nx,
  ny,
  resolveFeatureIconAndLabel,
} from '../flowCanvas.constants';

const HEADER_H = 44;
const SLOT_H = 54;
const ROW_H = 44;
const CONTENT_GAP = 10;
const BODY_PAD = 12;
const CONTROL_X = 88;
const CONTROL_W = LEADERBOARD_TRIGGER_W - CONTROL_X - BODY_PAD;

const TYPE_OPTIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'channel', label: 'Channel' },
];

const LEVEL_OPTIONS = [
  { value: 'global', label: 'Global' },
  { value: 'channel', label: 'Channel' },
  { value: 'stream', label: 'Stream' },
];

function SelectControl({ x, y, value, options, onChange }) {
  return (
    <foreignObject x={x} y={y} width={CONTROL_W} height="26" style={{ pointerEvents: 'auto' }}>
      <select
        value={value}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          onChange?.(event.target.value);
        }}
        style={{
          width: '100%',
          height: '24px',
          borderRadius: '12px',
          border: '1px solid rgba(145,70,255,0.35)',
          background: 'rgba(145,70,255,0.10)',
          color: '#ffffff',
          fontSize: '9px',
          fontWeight: 700,
          padding: '0 8px',
          outline: 'none',
          boxSizing: 'border-box',
          cursor: 'pointer',
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} style={{ background: '#1a1a2e', color: '#fff' }}>
            {option.label}
          </option>
        ))}
      </select>
    </foreignObject>
  );
}

function getLeaderboardValues(step, liveCustomisationData) {
  const cfg = step?.customisationConfig;
  const tabKey = cfg?.tabKey;
  const varKey = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const liveValues = tabKey && varKey && varName
    ? getPathValue(liveCustomisationData, [tabKey, varKey, varName])
    : null;
  const allValues = Array.isArray(liveValues)
    ? liveValues
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return allValues?.[0] || {};
}

export default function LeaderboardTriggerNode({
  step,
  selected,
  isDragOver,
  onActivate,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemove,
  onFieldChange,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  campaignFeatures,
}) {
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const left = x - LEADERBOARD_TRIGGER_W / 2;
  const top = y - LEADERBOARD_TRIGGER_H / 2;

  const values = getLeaderboardValues(step, liveCustomisationData);
  const featureId = values?.featureID || '';
  const saveType = values?.saveType || 'individual';
  const saveLevel = values?.saveLevel || 'global';
  const featureMeta = resolveFeatureIconAndLabel(featureId, campaignFeatures);
  const featureLabel = featureId
    ? (featureMeta.label.length > 28 ? `${featureMeta.label.slice(0, 26)}…` : featureMeta.label)
    : 'Drag a leaderboard here';

  const outline = selected ? C.sel : isDragOver ? C.green : C.purple;
  const slotTop = top + HEADER_H + BODY_PAD;
  const rowOneTop = slotTop + SLOT_H + CONTENT_GAP;
  const rowTwoTop = rowOneTop + ROW_H + CONTENT_GAP;

  return (
    <g
      className="fc-node fc-node--leaderboard-trigger"
      onClick={onActivate}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={left + 2}
        y={top + 4}
        width={LEADERBOARD_TRIGGER_W}
        height={LEADERBOARD_TRIGGER_H}
        rx="10"
        fill="#000"
        fillOpacity="0.3"
      />
      <rect
        x={left}
        y={top}
        width={LEADERBOARD_TRIGGER_W}
        height={LEADERBOARD_TRIGGER_H}
        rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={outline}
        strokeWidth={selected ? 2 : 1.5}
      />
      <line
        x1={left + 1}
        y1={top + HEADER_H}
        x2={left + LEADERBOARD_TRIGGER_W - 1}
        y2={top + HEADER_H}
        stroke={C.border}
        strokeWidth="1"
      />

      <text
        x={left + 14}
        y={top + 20}
        fontSize="11.5"
        fontWeight="700"
        fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {step.stepTitle || 'Leaderboard Trigger'}
      </text>
      <text
        x={left + 14}
        y={top + 34}
        fontSize="8.5"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Drag a leaderboard here only
      </text>

      <rect
        x={left + BODY_PAD}
        y={slotTop}
        width={LEADERBOARD_TRIGGER_W - BODY_PAD * 2}
        height={SLOT_H}
        rx="8"
        fill={featureId ? 'rgba(145,70,255,0.10)' : (isDragOver ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.03)')}
        stroke={featureId ? C.purple : (isDragOver ? C.green : C.border)}
        strokeWidth={1.2}
        strokeDasharray={featureId ? 'none' : '4 3'}
      />

      {featureId ? (
        <>
          <circle
            cx={left + BODY_PAD + 22}
            cy={slotTop + SLOT_H / 2}
            r="16"
            fill="rgba(145,70,255,0.15)"
            stroke="rgba(145,70,255,0.35)"
            strokeWidth="1"
          />
          <text
            x={left + BODY_PAD + 22}
            y={slotTop + SLOT_H / 2 + 5}
            textAnchor="middle"
            fontSize="14"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {featureMeta.icon}
          </text>
          <text
            x={left + BODY_PAD + 46}
            y={slotTop + SLOT_H / 2 + 4}
            fontSize="9.5"
            fontWeight="700"
            fill={C.text}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {featureLabel}
          </text>
          <g
            onClick={(event) => {
              event.stopPropagation();
              onRemove?.();
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            style={{ cursor: 'pointer' }}
          >
            <circle cx={left + LEADERBOARD_TRIGGER_W - BODY_PAD - 12} cy={slotTop + 14} r="10" fill="#dc2626" />
            <g transform={`translate(${left + LEADERBOARD_TRIGGER_W - BODY_PAD - 12}, ${slotTop + 14})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
              <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
              <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
              <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
              <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            </g>
          </g>
        </>
      ) : (
        <text
          x={left + LEADERBOARD_TRIGGER_W / 2}
          y={slotTop + SLOT_H / 2 + 4}
          textAnchor="middle"
          fontSize="10"
          fill={isDragOver ? '#dcfce7' : C.textMuted}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {featureLabel}
        </text>
      )}

      <text
        x={left + BODY_PAD}
        y={rowOneTop + 16}
        fontSize="9"
        fontWeight="700"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        TYPE
      </text>
      <SelectControl
        x={left + CONTROL_X}
        y={rowOneTop + 4}
        value={saveType}
        options={TYPE_OPTIONS}
        onChange={(value) => onFieldChange?.('saveType', value)}
      />

      <text
        x={left + BODY_PAD}
        y={rowTwoTop + 16}
        fontSize="9"
        fontWeight="700"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        LEVEL
      </text>
      <SelectControl
        x={left + CONTROL_X}
        y={rowTwoTop + 4}
        value={saveLevel}
        options={LEVEL_OPTIONS}
        onChange={(value) => onFieldChange?.('saveLevel', value)}
      />
    </g>
  );
}